/**
 * URL utilities for the visibility checker form.
 *
 * Pure functions – safe to call from server components, tests, and the
 * client-side form handler in `VisibilityCheckerInput`. No React or
 * browser-only globals here.
 */

import { stripWww } from '@/lib/url';

/**
 * Normalize URL input – add https:// when no protocol is present.
 */
export function normalizeUrl(input: string): string {
  let url = input.trim();
  if (!url) return '';
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`;
  }
  return url;
}

/**
 * Validate URL format. Must be http(s) and contain at least one dot.
 */
export function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    if (!parsed.hostname.includes('.')) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Fuzz reset time for abuse resistance. Scripts care about exact seconds; humans don't.
 */
export function fuzzyResetTime(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  if (minutes <= 5) return 'a few minutes';
  if (minutes <= 15) return 'about 15 minutes';
  if (minutes <= 30) return 'about half an hour';
  return 'about an hour';
}

/**
 * Extract a friendly display name from a URL hostname.
 */
export function extractNameFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const domain = stripWww(parsed.hostname);
    return domain
      .split('.')[0]
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } catch {
    return url;
  }
}
